import useEventCallback from '@unifyapps/hooks/useEventCallback';
import SvgChevronRight from '@unifyapps/icons/outline/ChevronRight';
import { Box } from '@unifyapps/ui/components/Box';
import { Divider } from '@unifyapps/ui/components/Divider';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import clsx from 'clsx';
import { type MouseEvent, memo, type ReactNode, useMemo, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { Link } from '../../../../navigation';
import { usePlatformThemeOverride } from '../../../../no-code/hooks/usePlatformThemeOverride';
import { groupContainsActiveNavItem } from '../../helpers';
import type { GroupedNavItem, NavApplicationItemType, NavItem, NavItemGroup } from '../../types';
import { getRedirectionProps, resolveOpenInNewTab } from '../../utils/redirection';
import { UA_PLATFORM_SIDE_NAV } from './constants';
import { subItemChevronVariants } from './styles';

interface Props {
  item: NavItemGroup;
  activeNavItem?: NavItem | NavApplicationItemType;
}

export const FLYOUT_OFFSET = {
  // Anchored to the inner collapsed rail button: 10px remaining rail inset + 5px visual gap.
  mainAxis: 15,
  // Center-aligns the first option with the collapsed rail button. First row center sits about
  // 53px below panel top; button center is 18px below trigger top.
  crossAxis: -55 + 18,
} as const;

// mirror of FLYOUT_OFFSET for bottom-half triggers, which anchor `right-end` so the panel grows
// upward: shift the panel down past the trigger so the bottom rows sit level with the button.
export const FLYOUT_OFFSET_BOTTOM = {
  mainAxis: 15,
  crossAxis: 55 - 18,
} as const;

// Popover content stays inside the flyout host DOM so CSS :hover can cover both the trigger and
// menu. The class still re-declares island tokens because this component depends on those vars.
// The shadow stays literal - it is the generic shadow-md, not an island colour.
const FLYOUT_PANEL_BASE_CLASSNAME = clsx(
  UA_PLATFORM_SIDE_NAV,
  'px-md pt-xl pb-lg !rounded-[12px] border bg-[var(--navbar-popover-bg)] border-[color:var(--navbar-popover-border)]',
  'shadow-[0_4px_8px_-2px_#EDEBE8,0_2px_4px_-2px_#EDEBE8]',
  // smooth open: fade + slight scale + slide in from the rail edge (panel opens to the right).
  // close is instant — the shared Popover unmounts its content on close, so there is no exit phase.
  'origin-left animate-in fade-in-0 zoom-in-95 slide-in-from-start-2 duration-150 ease-out',
);

/**
 * The semantic vars the island tokens resolve to (--bg-workspace, --text-tertiary, …) come from
 * the platform shell scope, so the panel used to hardcode `is-platform-shell`. That class must
 * follow the body's override decision (see apps/platform/src/app/layout.tsx): when the platform
 * theme is overridden the panel inherits the override cascade instead of pinning default values.
 */
export function useFlyoutPanelClassName() {
  const { themeOverrideClassName } = usePlatformThemeOverride();
  return clsx(FLYOUT_PANEL_BASE_CLASSNAME, themeOverrideClassName);
}

export const getFlyoutRowClassName = ({
  className,
  isActive,
}: {
  className?: string;
  isActive: boolean;
}) =>
  clsx(
    'rounded-sm transition-colors',
    isActive
      ? 'bg-[var(--navbar-popover-row-bg-active)] text-[color:var(--navbar-popover-row-fg-active)] hover:bg-[var(--navbar-popover-row-bg-active-hover)]'
      : 'text-[color:var(--navbar-popover-row-fg)] hover:bg-[var(--navbar-popover-row-bg-hover)] hover:text-[color:var(--navbar-popover-row-fg-hover)]',
    className,
  );

// the flyout renders inside the rail DOM, so a bare click on this header bubbles to
// SideNavigation.handleRailToggle and expands the rail. The header is a non-interactive label —
// stop the click here so it falls outside handleRailToggle's interactive-target allow-list path.
const stopHeaderClick = (event: MouseEvent<HTMLElement>) => event.stopPropagation();

export function NavigationFlyoutHeader({ children }: { children: ReactNode }) {
  return (
    <Box className="px-md pt-xxs cursor-default" onClick={stopHeaderClick}>
      <Typography
        as="span"
        className="uppercase leading-[12px] text-[color:var(--navbar-popover-header-fg)] tracking-[1.1px]"
        maxNumberOfLines={1}
        variant="text-xxs"
        weight="medium"
      >
        {children}
      </Typography>
    </Box>
  );
}

const flyoutRowLabel = (name: string) => (
  <Typography as="span" maxNumberOfLines={1} variant="text-sm" weight="regular">
    {name}
  </Typography>
);

const FlyoutRow = memo(function FlyoutRow({
  childItem,
  isActive,
}: {
  childItem: NavItem | NavApplicationItemType;
  isActive: boolean;
}) {
  const openInNewTabResolved = resolveOpenInNewTab(childItem);
  const redirectionProps = useMemo(
    () => getRedirectionProps(openInNewTabResolved),
    [openInNewTabResolved],
  );

  return (
    <Link
      className={getFlyoutRowClassName({ isActive, className: 'block px-md py-xs' })}
      href={childItem.url}
      {...redirectionProps}
    >
      {flyoutRowLabel(childItem.name)}
    </Link>
  );
});

const FlyoutGroupRow = memo(function FlyoutGroupRow({
  item,
  activeNavItem,
  isExpanded,
  onToggle,
}: {
  item: NavItemGroup;
  activeNavItem?: NavItem | NavApplicationItemType;
  isExpanded: boolean;
  onToggle: (id: string) => void;
}) {
  const containsActive = useMemo(
    () => groupContainsActiveNavItem(item, activeNavItem?.id),
    [item, activeNavItem?.id],
  );

  const { getCollapseProps, getToggleProps } = useCollapse({
    isExpanded,
    easing: 'ease-in-out',
    duration: 150,
  });

  const handleClick = useEventCallback(() => onToggle(item.id));

  return (
    <Stack className="gap-y-[2px]" direction="column">
      <Stack
        alignItems="center"
        // expanded groups drop the active fill (a visible child carries the real selection), matching
        // the rail accordion. native button so the rail's click-to-expand (handleRailToggle) skips it
        // instead of toggling the whole rail open — this replaces the old inert-row "opens rail" hack.
        className={getFlyoutRowClassName({
          isActive: containsActive && !isExpanded,
          className:
            'ua-platform-side-nav-flyout-row w-full ps-md pe-xs py-xs group/PlatformSideNavNestedItem',
        })}
        component="button"
        data-active={containsActive && !isExpanded}
        direction="row"
        justifyContent="space-between"
        {...getToggleProps()}
        onClick={handleClick}
      >
        {flyoutRowLabel(item.name)}
        <IconButton
          aria-hidden
          // rotate lives on the glyph, not the button: the button's pe is zeroed (asymmetric box),
          // so rotating the button would carry the missing end-inset to its bottom edge.
          className={`${subItemChevronVariants({
            expanded: false,
            active: containsActive,
          })} ua-platform-side-nav-chevron-button !size-5 !rounded-xs !pe-none`}
          color="neutral"
          component="span"
          Icon={SvgChevronRight}
          iconClassName={clsx(
            '!size-3 transition-transform',
            isExpanded && 'rotate-90',
            containsActive
              ? 'ua-side-nav-nested-item-active'
              : 'ua-side-nav-nested-item-active-false',
          )}
          size="sm"
          variant="ghost"
        />
      </Stack>
      <Box {...getCollapseProps()}>
        <Stack className="gap-x-sm ps-lg" direction="row">
          <Divider className="border-primary" orientation="vertical" />
          <FlyoutItems activeNavItem={activeNavItem} items={item.items} />
        </Stack>
      </Box>
    </Stack>
  );
});

function FlyoutItems({
  items,
  activeNavItem,
}: {
  items: GroupedNavItem[];
  activeNavItem?: NavItem | NavApplicationItemType;
}) {
  // one open sub-group per level, mirroring the expanded rail's activeSubGroupId
  const [expandedChildId, setExpandedChildId] = useState<string | null>(null);

  const handleToggle = useEventCallback((id: string) => {
    setExpandedChildId((prev) => (prev === id ? null : id));
  });

  return (
    <Stack className="gap-y-sm w-full py-xxs" direction="column">
      {items.map((childItem) =>
        childItem.type === 'GROUP' ? (
          <FlyoutGroupRow
            activeNavItem={activeNavItem}
            isExpanded={childItem.id === expandedChildId}
            item={childItem}
            key={childItem.id}
            onToggle={handleToggle}
          />
        ) : (
          <FlyoutRow
            childItem={childItem}
            isActive={childItem.id === activeNavItem?.id}
            key={childItem.id}
          />
        ),
      )}
    </Stack>
  );
}

export const NavigationGroupFlyout = (props: Props): ReactNode => {
  const { item, activeNavItem } = props;

  return (
    // gap-md = Figma's spacing-sm (6px) container gap folded with the first row's 2px top inset
    // (rows carry it inside the inter-row gap-y-sm, but the header has no bottom inset to pair with).
    <Stack className="gap-y-md" direction="column">
      <NavigationFlyoutHeader>{item.name}</NavigationFlyoutHeader>
      <FlyoutItems activeNavItem={activeNavItem} items={item.items} />
    </Stack>
  );
};
